import type React from 'react';
import { useCallback, useState } from 'react';

import { Paper } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { trackAnalyticsEvent } from '@ragu-components/ord-analytics';
import {
  PasswordInput,
  TextInput,
  SimpleForm,
  useNotify,
  SaveContextProvider,
  useTranslate,
  useGetIdentity,
} from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { OrdPasswordInput } from 'src/components/formInputs';
import { SimpleSaveToolbar } from 'src/components/forms';
import { useGetPasswordPolicy } from 'src/hooks/useGetPasswordPolicy';

import type { PasswordChangeRequest } from '@ragu-components/ord-types';
import type { FieldValues } from 'react-hook-form';

export const ChangePassword = (): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const { isLoading, passwordPolicy } = useGetPasswordPolicy({ cosType: 'personal' });

  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(
    (values: FieldValues) => {
      if (
        values.old_password === undefined ||
        values.new_password === undefined ||
        values.confirm_new_password === undefined ||
        values.new_password !== values.confirm_new_password ||
        values.old_password === values.new_password
      ) {
        notify('ord.settings.changePassword.changeError', { type: 'error' });
        return;
      }
      const params: PasswordChangeRequest = {
        old_password: values.old_password,
        new_password: values.new_password,
      };
      const totp = values.totp;
      if (typeof totp === 'string') {
        params.totp = totp;
      }
      setSaving(true);
      dataProvider
        .updatePassword(params)
        .then(() => {
          componentLogger.info('Password changed');
          trackAnalyticsEvent('password_changed', { event_category: 'security' });
          setSaving(false);
          notify('ord.settings.changePassword.changeSuccess', { type: 'info' });
        })
        .catch(() => {
          componentLogger.error('Password change failed');
          trackAnalyticsEvent('password_change_failed', { event_category: 'security' });
          setSaving(false);
          notify('ord.settings.changePassword.changeError', { type: 'error' });
        });
    },
    [dataProvider, notify]
  );

  const saveContext = {
    save: handleSave,
    saving,
  };

  return (
    <SaveContextProvider value={saveContext}>
      <Paper sx={{ overflow: 'hidden' }}>
        <SimpleForm onSubmit={handleSave} toolbar={<SimpleSaveToolbar />}>
          {!identityLoading &&
            identity !== undefined &&
            'totp_enabled' in identity &&
            identity.totp_enabled === true && (
              <TextInput
                sx={{ width: '100%' }}
                source='totp'
                label={translate('ord.settings.changePassword.sixDigitCode')}
                disabled={saving}
                required={true}
                validate={(value) => {
                  return value !== '' && value.length === 6
                    ? undefined
                    : translate('ord.settings.totp.sixDigitCodeLengthError');
                }}
              />
            )}
          <PasswordInput
            sx={{ width: '100%' }}
            source='old_password'
            autoComplete='new-password'
            label={translate('ord.settings.changePassword.oldPassword')}
            required={true}
            disabled={saving}
          />
          <OrdPasswordInput
            disabled={saving}
            init={false}
            isLoading={isLoading}
            label={translate('ord.settings.changePassword.newPassword')}
            passwordPolicy={passwordPolicy}
            required={true}
            source='new_password'
            sx={{ width: '100%' }}
            validate={(value, values) => {
              if (value !== values.confirm_new_password) {
                return translate('ord.settings.changePassword.passwordsDoNotMatch');
              }
              return undefined;
            }}
            tooltip={translate('resources.users.operations.genPassword')}
          />
          <PasswordInput
            autoComplete='new-password'
            sx={{ width: '100%' }}
            source='confirm_new_password'
            label={translate('ord.settings.changePassword.confirmPassword')}
            required={true}
            disabled={saving}
            validate={(value, values) => {
              if (value !== values.new_password) {
                return translate('ord.settings.changePassword.passwordsDoNotMatch');
              }
              return undefined;
            }}
          />
        </SimpleForm>
      </Paper>
    </SaveContextProvider>
  );
};
